import { ConsumptionElement } from '@vae/server/dist/client'
import { Dayjs } from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { trpc } from '../../modules/trpc'
import { ObjectContext } from '../objectContext/ObjectContext'
import useUser from './useUser'

export interface ConsumptionElementWithStats extends ConsumptionElement {
	totalConsumption: number
	avgExchangePrice: number
	percentage: number
	eNodeId?: string
	eNodeType?: 'electricCar' | 'solarPanel'
	elementId?: number
}

export const useConsumptionElementData = (
	startTime?: Dayjs,
	endTime?: Dayjs,
	language?: 'fi' | 'en'
) => {
	const { price, ...object } = useContext(ObjectContext)

	const { user } = useUser()
	const [elementData, setElementData] = useState<ConsumptionElementWithStats[]>([])
	const [chartData, setchartData] = useState<number[]>([])
	const [chartLabels, setChartLabels] = useState<string[]>([])
	const [totalConsumption, setTotalConsumption] = useState(0)

	if (!startTime || !endTime)
		return {
			data: [],
			loading: true,
			chartElement: null,
			inverters: [],
		}

	const { data, isLoading } = trpc.objects.getEnergyReport.useQuery(
		{
			objectId: object.id,
			from: startTime.format(),
			to: endTime.format(),
		},
		{
			staleTime: 1000 * 60 * 60, // 1 hour
			enabled: !!object.id && !!startTime && !!endTime && !!user?.hashedPid,
		}
	)

	const { data: inverters } = trpc.eNode.inverter.getAll.useQuery(
		{
			hashedPid: user!.hashedPid,
		},
		{
			staleTime: 2 * 1000 * 60, // 2 mins
			enabled: user?.panelLinked && Boolean(user?.hashedPid),
		}
	)

	useEffect(() => {
		if ((data?.analyzedData.length ?? 0) > 0) parseData(data)
	}, [data])

	const parseData = async (data: any) => {
		setElementData([])

		const baselineTotal = data.analyzedData.reduce(
			(acc: number, curr: any) => acc + curr.output.baseline.value,
			0
		)

		const withBaseline = [
			{
				totalConsumption: baselineTotal,
				name: object.hasElectricHeating
					? language === 'fi'
						? 'Lämmitys ja kylmälaitteet'
						: 'Heating and cooling devices'
					: language === 'fi'
						? 'Kylmälaitteet'
						: 'Cooling devices',
				avgExchangePrice: price.weeklySpotAverage,
			},
			...data.elements,
		].sort((a: any, b: any) => (a.totalConsumption > b.totalConsumption ? -1 : 1))

		const totalConsumption = withBaseline.reduce(
			(acc: number, curr: any) => acc + curr.totalConsumption,
			0
		)

		const withPercentages = withBaseline.map((element: any) => ({
			...element,
			percentage: Math.round((element.totalConsumption / totalConsumption) * 100),
			eNodeId: element.eNodeId,
		}))

		setTotalConsumption(totalConsumption)

		if (data.elements.length > 0) setElementData(withPercentages)

		setchartData(withPercentages.map((element: any) => element.totalConsumption))

		setChartLabels(withPercentages.map((element: any) => element.name))
	}

	return {
		data: elementData,
		loading: isLoading,
		chartData,
		chartLabels,
		totalConsumption,

		inverters: inverters ?? [],
	}
}
